// errorMessages.js
// Centralized error message formatting for user-friendly, professional error displays

/**
 * Detect if user is using Brave browser
 */
const isBraveBrowser = () => {
  return navigator.brave && typeof navigator.brave.isBrave === 'function';
};

/**
 * Format Firebase Authentication errors into user-friendly messages
 * @param {Error} error - Firebase error object
 * @returns {string} - User-friendly error message
 */
export const formatAuthError = (error) => {
  const code = error?.code || '';
  const message = error?.message || '';

  // Network and connectivity errors
  if (
    message.includes('client is offline') || 
    code === 'unavailable' || 
    code === 'auth/network-request-failed'
  ) {
    if (isBraveBrowser()) {
      return 'Unable to connect to authentication service. Please disable Brave Shields for this site by clicking the lion icon in your address bar, or use a different browser.';
    }
    return 'Unable to connect to the authentication service. Please check your internet connection and try again.';
  }

  // Authentication-specific errors
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
      return 'The email or password you entered is incorrect. Please try again.';

    case 'auth/user-not-found':
      return 'No account exists with this email address. Please sign up to create an account.';

    case 'auth/invalid-email':
      return 'The email address format is invalid. Please enter a valid email address.';

    case 'auth/email-already-in-use':
      return 'An account with this email address already exists. Please log in or use a different email.';

    case 'auth/weak-password':
      return 'The password is too weak. Please use at least 8 characters with a mix of letters and numbers.';

    case 'auth/too-many-requests':
      return 'Too many unsuccessful attempts. Please wait a few minutes before trying again.';

    case 'auth/operation-not-allowed':
      return 'This sign-in method is not enabled. Please contact support.';

    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact support for assistance.';

    case 'auth/requires-recent-login':
      return 'For security reasons, please log out and log back in before performing this action.';

    case 'auth/popup-blocked':
    case 'auth/popup-closed-by-user':
      if (isBraveBrowser()) {
        return 'Authentication popup was blocked. Please disable Brave Shields for this site or allow popups in your browser settings.';
      }
      return 'Authentication popup was blocked. Please allow popups for this site in your browser settings.';

    case 'auth/unauthorized-domain':
      return 'This domain is not authorized for authentication. Please contact support.';

    case 'auth/account-exists-with-different-credential':
      return 'An account already exists with the same email but different sign-in credentials. Please use your original sign-in method.';

    case 'auth/invalid-action-code':
      return 'This verification link is invalid or has expired. Please request a new verification email.';

    case 'auth/expired-action-code':
      return 'This verification link has expired. Please request a new verification email.';

    default:
      // Generic fallback with Brave hint if applicable
      if (isBraveBrowser() && (message.includes('Failed') || message.includes('blocked'))) {
        return 'Authentication failed. Try disabling Brave Shields for this site or use a different browser.';
      }
      return 'An unexpected error occurred. Please try again or contact support if the issue persists.';
  }
};

/**
 * Format Firestore errors into user-friendly messages
 * @param {Error} error - Firestore error object
 * @returns {string} - User-friendly error message
 */
export const formatFirestoreError = (error) => {
  const code = error?.code || '';
  const message = error?.message || '';

  switch (code) {
    case 'permission-denied':
      return 'You do not have permission to access this resource. Please ensure you are logged in.';

    case 'not-found':
      return 'The requested information could not be found.';

    case 'already-exists':
      return 'This item already exists.';

    case 'resource-exhausted':
      return 'Service quota exceeded. Please try again later.';

    case 'failed-precondition':
      return 'The operation was rejected. Please ensure all required conditions are met.';

    case 'aborted':
      return 'The operation was aborted due to a conflict. Please try again.';

    case 'out-of-range':
      return 'The requested operation is out of range.';

    case 'unimplemented':
      return 'This feature is not yet implemented.';

    case 'internal':
      return 'An internal server error occurred. Please try again later.';

    case 'unavailable':
      if (isBraveBrowser()) {
        return 'Unable to connect to the database. Please disable Brave Shields for this site or use a different browser.';
      }
      return 'The database service is temporarily unavailable. Please check your connection and try again.';

    case 'unauthenticated':
      return 'You must be logged in to perform this action.';

    case 'deadline-exceeded':
      return 'The operation took too long. Please try again.';

    default:
      if (message.includes('offline') || message.includes('network')) {
        return 'Network connection issue. Please check your internet connection and try again.';
      }
      return 'An error occurred while accessing the database. Please try again.';
  }
};

/**
 * Format Storage errors into user-friendly messages
 * @param {Error} error - Firebase Storage error object
 * @returns {string} - User-friendly error message
 */
export const formatStorageError = (error) => {
  const code = error?.code || '';

  switch (code) {
    case 'storage/unauthorized':
      return 'You do not have permission to upload this file. Please ensure you are logged in.';

    case 'storage/canceled':
      return 'File upload was canceled.';

    case 'storage/unknown':
      return 'An unknown error occurred during file upload. Please try again.';

    case 'storage/object-not-found':
      return 'The requested file could not be found.';

    case 'storage/bucket-not-found':
      return 'Storage bucket not found. Please contact support.';

    case 'storage/project-not-found':
      return 'Project configuration error. Please contact support.';

    case 'storage/quota-exceeded':
      return 'Storage quota exceeded. Please contact support.';

    case 'storage/unauthenticated':
      return 'You must be logged in to upload files.';

    case 'storage/retry-limit-exceeded':
      return 'Upload failed after multiple attempts. Please check your connection and try again.';

    case 'storage/invalid-checksum':
      return 'File upload verification failed. Please try uploading the file again.';

    case 'storage/invalid-event-name':
      return 'Invalid upload operation.';

    case 'storage/invalid-url':
      return 'Invalid file URL.';

    case 'storage/invalid-argument':
      return 'Invalid file or upload parameters.';

    case 'storage/no-default-bucket':
      return 'No storage bucket configured. Please contact support.';

    case 'storage/cannot-slice-blob':
      return 'File upload error. Please try selecting the file again.';

    case 'storage/server-file-wrong-size':
      return 'File size mismatch. Please try uploading again.';

    default:
      return 'File upload failed. Please try again or contact support if the issue persists.';
  }
};

/**
 * Format validation errors into user-friendly messages
 * @param {string} field - Field name
 * @param {string} validationType - Type of validation error
 * @returns {string} - User-friendly error message
 */
export const formatValidationError = (field, validationType) => {
  const fieldName = field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1');

  switch (validationType) {
    case 'required':
      return `${fieldName} is required.`;
    case 'email':
      return 'Please enter a valid email address.';
    case 'isu-email':
      return 'Please use a valid ISU email address (@ilstu.edu).';
    case 'password-length':
      return 'Password must be at least 8 characters long.';
    case 'password-number':
      return 'Password must contain at least one number.';
    case 'password-match':
      return 'Passwords do not match.';
    case 'min-value':
      return `${fieldName} must be greater than or equal to the minimum value.`;
    case 'max-value':
      return `${fieldName} must be less than or equal to the maximum value.`;
    case 'invalid-format':
      return `${fieldName} format is invalid.`;
    default:
      return `${fieldName} is invalid.`;
  }
};
